import { useCallback, useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { FaWind } from 'react-icons/fa6';
import { HiOutlineEye } from 'react-icons/hi2';
import { IoCheckmarkDoneSharp } from 'react-icons/io5';
import { MdOutlineInsertPhoto, MdOutlineModeEditOutline } from "react-icons/md";
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Viewer from 'react-viewer';
import { ActionIcon, Button, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import StatusDropdown from '../../../components/forms/StatusDropdown';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useCarrionStatus from '../../../hooks/api-calls/useCarrionStatus';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { RootState } from '../../../redux/store';
import { menuRoutes } from '../../../router/menu';
import { CarrionService } from '../../../services/carrion/carrionService';
import { DocumentsService } from '../../../services/document/documentsService';
import { ListApiResponse } from '../../../type/entities/apiResponse-type';
import { windDirectionsSpanish, windSpeed, age } from '../../../utils/SelectorMapping';
import CarrionFormModal from '../components/CarrionFormModal';
import CarrionFilters from './CarrionFilters';
import { ImportCarrionModal } from './components/ImportCarrionModal';

interface CarrionListProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    isOpenImport: boolean;
    setIsOpenImport: (isOpen: boolean) => void;
}

const columnOrder = [
    'id',
    'dateReported',
    'windDirection',
    'windSpeed',
    'cloudCoverage',
    'coordinates',
    'windTurbine',
    'distanceFromTurbine',
    'species',
    'notifiedOwner',
    'nameOwner',
    'avianPresencia',
    'turbineOff',
    'age',
    'notifiedEnvAgency',
    'comments',
    'carrionPhotos',
    'carrionStatus',
    'carrionId',
    'park',
    'user',
];

const CarrionList: React.FC<CarrionListProps> = ({ isOpen, setIsOpen, isOpenImport, setIsOpenImport }) => {

    // STATES

    const [carrion, setCarrion] = useState(null);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isViewerOpen, setIsViewerOpen] = useState(null);
    const [isLoadingImages, setIsLoadingImages] = useState<boolean>(false);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [viewerImages, setViewerImages] = useState<any>(null);

    // HOOKS

    const { user } = useSelector((state: RootState) => state.auth);
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { getCarrionStatusList } = useCarrionStatus();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const carrionService = new CarrionService();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE ESPECIES
     * @EN GETS THE LIST OF SPECIES
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await carrionService.listCarrions(filters);
        return response.getResponseData() as ListApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELIMINACIÓN DE UNA ESPECIE
     * @EN HANDLES THE DELETION OF A SPECIES
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await carrionService.deleteCarrions(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Especie eliminada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    // -------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE VARIAS ESPECIES
     * @EN HANDLES THE DELETION OF MULTIPLE SPECIES
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = await (await carrionService.deleteMultiCarrions(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Usuarios eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    // -------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    // -------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR DE ESTADO LA CARROÑA
     * @EN CHANGE THE STATUS OF THE CARRION
     * 
     * @param id
     * @param status
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleChangeStatus = async (id: string, status: string) => {
        const response = await (await carrionService.changeStatusCarrion(id, status)).getResponseData();
        if (response.success) {
            toast.success("Estado de la carroña cambiada correctamente");
            refetch();
        } else {
            handleErrors(response);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------

    // ----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL CERRADO DEL MODAL
     * @EN HANDLES THE CLOSING OF THE MODAL
     */
    // ----------------------------------------------------------------------------------------------------------------------
    const onCloseModal = () => {
        setIsOpen(false);
        setTimeout(() => {
            setCarrion(null);
        }, 150);
    }
    // ---------------------------------------------------------------------------------------------------------------------

    const onCloseImportModal = () => {
        setIsOpenImport(false);
    }



    const handleOpenViewer = async (carrionRow: any) => {
        setIsLoadingImages(true);
        if (!carrionRow.carrionPhotos || carrionRow.carrionPhotos.length === 0) return;
        const images = await Promise.all(
            (carrionRow.carrionPhotos || []).map(async (photo: any) => {
                const response = await new DocumentsService().renderDocument(photo.document.id);
                const file = new Blob([response?.getResponse()?.data], { type: 'image/' + photo.document.extension });
                return {
                    ...photo,
                    src: URL.createObjectURL(file),
                };
            })
        );
        setViewerImages(images);
        setIsViewerOpen(carrionRow.id);
        setIsLoadingImages(false);
    };

    // RENDER

    return (
        <Page container='fluid'>
            <CustomTable
                id={'carrion-table'}
                divClassName='max-h-table'
                classNameTable='fs-table'
                headerTableClassName='fs-table-header'
                columnOrder={columnOrder}
                data={orderedData()}
                isLoading={loading}
                columnsNotShown={['windSpeed', 'nameOwner']}
                isSortable
                multiActions={[
                    {
                        label: 'Validar seleccionados',
                        render: (selectedRows: any, selectedRowsOriginal: any, reset: Function) => {
                            return (
                                <Button
                                    variant="outline"
                                    color="primary"
                                    className="flex items-center border-green text-green"
                                    disabled={isValidating}
                                    onClick={async () => {
                                        try {
                                            setIsValidating(true);
                                            const response = (await carrionService.validateSelectedCarrions(selectedRows));
                                            if (response) {
                                                toast.success('Carroñas validadas correctamente');
                                                refetch();
                                            } else {
                                                toast.error('Error al validar las carroñas');
                                            }
                                        } catch (error: any) {
                                            toast.error(error.message);
                                        } finally {
                                            setIsValidating(false);
                                            reset();
                                        }
                                    }}
                                >
                                    <IoCheckmarkDoneSharp className="h-[20px] w-[20px] mr-2" />
                                    {isValidating ? <FaSpinner className="animate-spin h-[20px] w-[20px] mr-2" /> : <span>Validar</span>}
                                </Button>
                            )
                        }
                    }
                ]}
                overrideColumns={[
                    {
                        key: 'comments',
                        customHeader: 'OBS',
                        render: (row: any) => {
                            return (
                                <div className="flex flex-col gap-2">
                                    <Text className="text-center text-capitalize">
                                        {row.row.original.comments || '-'}
                                    </Text>
                                </div>
                            )
                        }
                    },
                    {
                        key: 'carrionId',
                        customHeader: 'Código',
                        render: (row: any) => {
                            return (<a href={`${menuRoutes.carrion.path}/${row.row.original.id}/profile/${menuRoutes.carrion.profile.info}`} className='text-primary hover:text-primary hover:underline'>
                                {row.row.original.carrionId || '-'}
                            </a>);
                        }
                    },
                    {
                        key: 'client',
                        render: (row: any) => {
                            return row.row.original.park?.client?.name || '-';
                        }
                    },
                    {
                        key: 'park',
                        customHeader: 'Parque Eólico',
                        render: (row: any) => {
                            return row.row.original.park?.name || '-';
                        }
                    },
                    {
                        key: 'windTurbine',
                        customHeader: 'Aero',
                        render: (row: any) => {
                            return row.row.original.windTurbine?.alias || '-';
                        }
                    },
                    {
                        key: 'user',
                        customHeader: 'Observador',
                        render: (row: any) => {
                            return row.row.original.user?.name + ' ' + row.row.original.user.lastName || '-';
                        }
                    },
                    {
                        key: 'species',
                        customHeader: 'Especie',
                        render: (row: any) => {
                            return (
                                <div className='italic text-center'>
                                    {row.row.original.species?.scientificName || '-'}
                                </div>
                            )
                        }
                    },
                    {
                        key: 'distanceFromTurbine',
                        customHeader: 'Dist (m)',
                        render: (row: any) => {
                            return (
                                <div className="flex flex-col gap-2">
                                    <Text className="text-center text-capitalize">
                                        {row.row.original.distanceFromTurbine || '-'} {row.row.original.distanceFromTurbine ? 'm' : ''}
                                    </Text>
                                </div>
                            )
                        }
                    },
                    {
                        key: 'windDirection',
                        customHeader: 'D_VTO',
                        render: (row: any) => {
                            return (
                                <div className="flex flex-col gap-2">
                                    <Text className="text-center capitalize flex items-center">
                                        <FaWind className="h-3 w-3 me-1 text-primary" size={20} />
                                        {windDirectionsSpanish.find((item) => item.value == row.row.original.windDirection)?.label || '-'},
                                    </Text>
                                    <Text className="text-center fs-table font-bold text-gray-500">
                                        {windSpeed.find((item) => item.value == row.row.original.windSpeed)?.label.split('-')[1].trim() || '-'}
                                    </Text>
                                </div>
                            )
                        }
                    },
                    {
                        key: 'cloudCoverage',
                        customHeader: 'COB',
                        render: (row: any) => {
                            return (
                                <div className="flex flex-col gap-2">
                                    <Text className="text-center text-capitalize">
                                        {row.row.original.cloudCoverage || '-'}
                                    </Text>
                                </div>
                            )
                        }
                    },
                    {
                        key: 'notifiedEnvAgency',
                        customHeader: 'AMA',
                        render: (row: any) => {
                            return (
                                <div className="flex flex-col gap-2">
                                    <Text className="text-center text-capitalize">
                                        {row.row.original.notifiedEnvAgency ? 'Sí' : 'No'}
                                    </Text>
                                </div>
                            )
                        }
                    },
                    {
                        key: 'avianPresencia',
                        customHeader: 'P_AVES',
                        render: (row: any) => {
                            return (
                                <div className="flex flex-col gap-2">
                                    <Text className="text-center text-capitalize">
                                        {row.row.original.avianPresencia ? 'Sí' : 'No'}
                                    </Text>
                                </div>
                            )
                        }
                    },
                    {
                        key: 'turbineOff',
                        customHeader: 'AERO_OF',
                        render: (row: any) => {
                            return (
                                <div className="flex flex-col gap-2">
                                    <Text className="text-center text-capitalize">
                                        {row.row.original.turbineOff ? 'Sí' : 'No'}
                                    </Text>
                                </div>
                            )
                        }
                    },
                    {
                        key: 'age',
                        render: (row: any) => {
                            return (
                                <div className="flex flex-col gap-2">
                                    <Text className="text-capitalize">
                                        {age.find((item) => item.value == row.row.original.age)?.label || '-'}
                                    </Text>
                                </div>
                            )
                        }
                    },
                    {
                        key: 'notifiedOwner',
                        customHeader: 'Propietario',
                        render: (row: any) => {
                            return (
                                <div className="flex flex-col gap-2">
                                    <Text className="text-center text-capitalize">
                                        {row.row.original.notifiedOwner ? 'Sí' : 'No'}
                                    </Text>
                                    {row.row.original.notifiedOwner === true ? (
                                        <Text className="text-center text-xs  text-gray-500">
                                            <span className='font-bold'> Propietario: </span>{row.row.original.nameOwner}
                                        </Text>
                                    ) : null}
                                </div>
                            )
                        }
                    },
                    {
                        key: 'carrionStatus',
                        customHeader: 'Estado',
                        render: (row: any) => {
                            const carrionStatus = row.row.original?.carrionStatus;
                            const carrionId = row.row.original?.id;
                            return (
                                <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.customId + carrionStatus.id}>
                                    {(carrionId && carrionStatus) ? (
                                        <StatusDropdown
                                            divClassName='fs-table'
                                            selectedClassName='fs-table'
                                            optionsClassName='fs-table'
                                            entityId={row.row.original?.id}
                                            title="¿Estas seguro de cambiar el estado de la carroña?"
                                            statesOptions={getCarrionStatusList()}
                                            currentState={carrionStatus}
                                            disabled={!userCan('edit', 'carrion')}
                                            handleStateChange={(entityId: string, statusId: string) => handleChangeStatus(carrionId, statusId)}
                                            hasComments={false}
                                        />
                                    ) : "N/A"}
                                </div>
                            );
                        }
                    },
                    {
                        key: 'carrionPhotos',
                        customHeader: 'Fotos',
                        render: (row: any) => {
                            return (
                                <p className="flex items-center gap-2">
                                    <MdOutlineInsertPhoto
                                        className="h-6 w-6"
                                        size={20}
                                        onClick={() => handleOpenViewer(row.row.original)}
                                    />
                                    <span>{row.row.original.carrionPhotos?.length || 0}</span>
                                    {row.row.original.carrionPhotos?.length !== 0 ? <Viewer
                                        visible={isViewerOpen === row.row.original.id}
                                        onClose={() => setIsViewerOpen(null)}
                                        images={viewerImages?.map((img: any) => ({ src: img.src, alt: img.document.name }))}
                                    />
                                        : null}
                                </p>
                            );
                        }
                    },
                    {
                        key: 'coordinates',
                        customHeader: 'Coordenadas',
                        render: (row: any) => {
                            const [h, x, y] = row.row.original.coordinates.split(',');
                            return (
                                <>
                                    <p key="h">H: {h}S</p>
                                    <p key="x">X: {x}</p>
                                    <p key="y">Y: {y}</p>
                                </>
                            );
                        }
                    },
                ]}
                actions={[
                    {
                        label: 'View',
                        permissions: { group: 'carrion', permission: 'get' },
                        render: (row: any) => {
                            return (
                                <Tooltip
                                    size="sm"
                                    content={'Ver carroña'}
                                    placement="top"
                                    color="invert"
                                >
                                    <Link
                                        to={`${menuRoutes.carrion.path}/${row.id}/profile/${menuRoutes.carrion.profile.info}`}>
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white h-5 w-5"
                                        >
                                            <HiOutlineEye className="h-3 w-3" size={20} />
                                        </ActionIcon>
                                    </Link>
                                </Tooltip>
                            );
                        },
                    },
                    {
                        label: 'Edit',
                        permissions: { group: 'carrion', permission: 'edit' },
                        render: (row: any) => {
                            return (
                                <Tooltip
                                    size="sm"
                                    content={'Editar carroña'}
                                    placement="top"
                                    color="invert"
                                >
                                    <ActionIcon
                                        as="span"
                                        size="sm"
                                        variant="outline"
                                        className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer h-5 w-5"
                                        onClick={() => {
                                            setIsOpen(true)
                                            setCarrion(row.id);
                                        }}
                                    >
                                        <MdOutlineModeEditOutline className="h-3 w-3" size={20} />
                                    </ActionIcon>
                                </Tooltip>
                            );
                        },
                    },
                    {
                        label: 'Delete',
                        permissions: { group: 'carrion', permission: 'delete' },
                        render: (row: any) => {
                            if (row.id === user?.id) return null;
                            return (
                                <Tooltip
                                    size="sm"
                                    content={'Eliminar carroña'}
                                    placement="top"
                                    color="invert"
                                >
                                    <div>
                                        <DeletePopover
                                            actionIconClassName='h-5 w-5'
                                            title={`Eliminar carroña`}
                                            description={`¿Estás seguro de que deseas eliminar la carroña ${row.carrionId}?`}
                                            onDelete={() => handleDelete(row.id)}
                                        />
                                    </div>
                                </Tooltip>
                            );
                        },
                    },
                ]}
                filters={filters}
                updateFilters={updateFilters}
                handleMultipleDelete={handleMultiDelete}
                updateFilterOrder={updateFilterOrder}
                defaultOrder={filters.filter_order || undefined}
                paginationData={{
                    pageSize: filters.limit,
                    currentPage: filters.page,
                    pageCount: (data as ListApiResponse) ? data.lastPage : 1,
                    totalCount: data?.totalRegisters,
                    handlePagination: updatePage,
                    handlePerPage: updatePageSize,
                }}
                toggleFilters={() => setOpenFilters(!openFilters)}
            />

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros de Carroñas'}>
                <CarrionFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>

            <CarrionFormModal
                isOpen={isOpen}
                onClose={onCloseModal}
                carrion={carrion}
                refetch={refetch}
            />

            <ImportCarrionModal
                isOpen={isOpenImport}
                onClose={onCloseImportModal}
                refetch={refetch}
            />

        </Page>
    );
};

export default CarrionList;
